// umlaut-check: skip — Funktionsnamen wie "uebernehmen"/"ausloesen" sind
// technische Bezeichner (Variablen-/Funktionsnamen bleiben ASCII).

import React, {useEffect, useId, useRef, useState} from "react";
import {Overlay} from "react-bootstrap";
import axios from "axios";
import {formatFarbnummer} from "./format.js";

/**
 * Artikel-Suchfeld der Lieferpositionen-Erfassung (AP5, Auftrag Punkt 3):
 * Freitexteingabe mit Vorschlagsliste aus GET
 * "/cosmos/daten/lieferungen/artikelsuche" (Farbnummer "231"/"#231", Name
 * "signalweiß", Artikelnummer). Tastatur: Pfeile blättern, Enter übernimmt
 * den aktiven Vorschlag, Esc schließt ohne Änderung. Ohne Treffer bleibt
 * die eingetippte Bezeichnung als Freitext stehen (Zeile ohne
 * Artikeltreffer, PLAN.md Abschnitt 6) - kein natives Auswahlelement.
 *
 * @param {string} artikelNummer - aktueller Wert der Zeile.
 * @param {string} anzeige - aktuell angezeigter Text (Bezeichnung bzw. Artikelnummer).
 * @param {(treffer: object) => void} onWahl - ein Vorschlag wurde übernommen.
 * @param {(text: string) => void} onFreitext - Feld verlassen ohne Übernahme, Text als Freitext.
 * @param {boolean} [artikelBekannt] - true, wenn der aktuelle Wert bekannt ist (sonst Markierung "ohne Treffer").
 */
export default function ArtikelSuchfeld({id, artikelNummer, anzeige, onWahl, onFreitext, artikelBekannt = true, disabled = false}) {
    const generatedId = useId();
    const instanceId = id || `cosmos-artikel-suche-${generatedId}`;
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const zuletztCommittedRef = useRef(anzeige || "");

    const [query, setQuery] = useState(anzeige || "");
    const [treffer, setTreffer] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [suche, setSuche] = useState(false);

    useEffect(() => {
        setQuery(anzeige || "");
        zuletztCommittedRef.current = anzeige || "";
        // Nur reagieren, wenn sich der Wert VON AUSSEN ändert (z. B. nach dem
        // Speichern) - nicht bei jedem lokalen Tastendruck.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artikelNummer]);

    const sucheAusloesen = (text) => {
        clearTimeout(debounceRef.current);
        if (text.trim().length < 2) {
            setTreffer([]);
            setOpen(false);
            return;
        }
        debounceRef.current = setTimeout(() => {
            setSuche(true);
            axios
                .get("/cosmos/daten/lieferungen/artikelsuche", {params: {q: text}})
                .then(({data}) => {
                    setTreffer(data.treffer || []);
                    setOpen(true);
                    setActiveIndex(data.treffer && data.treffer.length ? 0 : -1);
                })
                .catch(() => {
                    setTreffer([]);
                })
                .finally(() => setSuche(false));
        }, 250);
    };

    const uebernehmen = (index) => {
        const t = treffer[index];
        if (!t) {
            return;
        }
        onWahl(t);
        zuletztCommittedRef.current = t.bezeichnung || t.farbname || t.artikel_nummer;
        setQuery(zuletztCommittedRef.current);
        setOpen(false);
        setTreffer([]);
    };

    const schliessenUndFreitext = () => {
        setOpen(false);
        if (query !== zuletztCommittedRef.current) {
            onFreitext(query);
            zuletztCommittedRef.current = query;
        }
    };

    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setQuery(zuletztCommittedRef.current);
            setOpen(false);
            return;
        }
        if (!open || treffer.length === 0) {
            return;
        }
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex((i) => Math.min(treffer.length - 1, i + 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex((i) => Math.max(0, i - 1));
                break;
            case "Enter":
                e.preventDefault();
                e.stopPropagation();
                uebernehmen(activeIndex >= 0 ? activeIndex : 0);
                break;
            default:
                break;
        }
    };

    const zeigeMarkierung = artikelNummer && !artikelBekannt;

    return (
        <>
            <input
                ref={inputRef}
                id={instanceId}
                type="text"
                className={`form-control form-control-sm${zeigeMarkierung ? " border-warning" : ""}`}
                style={{minWidth: 180}}
                value={query}
                disabled={disabled}
                autoComplete="off"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-controls={`${instanceId}-liste`}
                placeholder="Farbnummer, Name oder Artikelnr."
                onChange={(e) => {
                    setQuery(e.target.value);
                    sucheAusloesen(e.target.value);
                }}
                onFocus={() => {
                    if (treffer.length > 0) {
                        setOpen(true);
                    }
                }}
                onBlur={() => window.setTimeout(schliessenUndFreitext, 120)}
                onKeyDown={handleKeyDown}
            />

            <Overlay target={inputRef} show={open && treffer.length > 0} placement="bottom-start" flip rootClose onHide={() => setOpen(false)}>
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        id={`${instanceId}-liste`}
                        role="listbox"
                        className="bg-white border rounded-1 shadow-sm py-1"
                        style={{...overlayProps.style, minWidth: 260, maxHeight: 260, overflowY: "auto", zIndex: 3000}}
                    >
                        {treffer.map((t, index) => (
                            <div
                                key={t.artikel_nummer}
                                role="option"
                                aria-selected={index === activeIndex}
                                className={`px-2 py-1 small d-flex align-items-center gap-2${index === activeIndex ? " bg-primary text-white" : ""}`}
                                style={{cursor: "pointer"}}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => uebernehmen(index)}
                            >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        display: "inline-block",
                                        width: 12,
                                        height: 12,
                                        borderRadius: 2,
                                        backgroundColor: t.farbwert || "#e9ecef",
                                        border: "1px solid rgba(0,0,0,.15)",
                                        flexShrink: 0,
                                    }}
                                />
                                <span className="flex-grow-1">
                                    {/* #24-Rest (Sichtprüfung 2, 22.09.2026): "#016" statt "#16" - dreistellig mit führenden Nullen. */}
                                    {t.farbnummer !== null ? `${formatFarbnummer(t.farbnummer, t.farbnummer_unter)} ` : ""}
                                    {t.bezeichnung || t.farbname}
                                </span>
                                <span className="text-muted">{t.artikel_nummer}</span>
                            </div>
                        ))}
                    </div>
                )}
            </Overlay>
            {suche && <span className="visually-hidden">Suche läuft…</span>}
        </>
    );
}
